import {
  forwardRef,
  StrictMode,
  useEffect,
  useRef,
  useState,
  type FormEvent,
} from 'react'
import { useTheme } from '@opallabs/hooks'
import { TextInputType } from '../TextInput/types'
import {
  TextInputWithLabel,
  TextInputImperativeHandle,
} from '../TextInputWithLabel'
import { screenreaderOnly } from '../utils'
import * as styles from './styles'
import type { SearchInputProps } from './types'

export type { SearchInputProps }

function blockSubmit(event: FormEvent<HTMLFormElement>): void {
  event.preventDefault()
}

/**
 * Accessible, search-specific wrapper for the Lapidary `<TextInput>`.
 *
 * You should pass a `controlsHtmlId` with the HTML `id` attribute of the
 * element that displays this input’s search results. That element should be
 * marked as `aria-live="polite"`, so that screenreaders report updates.
 *
 * @example
 * import { useRef, useState } from 'react'
 * import uniqueId from 'lodash/uniqueId'
 * import { SearchInput } from '@opallabs/lapidary'
 *
 * const data = ['apple', 'peach', 'pear', 'plum']
 * export const Fruits = ({
 *   data,
 * }) => {
 *   const [search, setSearch] = useState('')
 *   const [filteredData, setFilteredData] = useState(data)
 *   useEffect(() => {
 *     const pattern = new RegExp(search, 'i')
 *     setFilteredData(data.filter(fruit => pattern.test(fruit)))
 *   }, [data, search])
 *   const htmlId = useRef(uniqueId('fruits'))
 *   return (
 *     <div>
 *       <SearchInput
 *         controlsHtml={htmlId.current}
 *         onSearch={(text) => setSearch(text)}
 *       />
 *       <div>
 *         <h1>Fruit</h1>
 *         <ul
 *           aria-live="polite"
 *           id={htmlId.current}
 *         >
 *         {filteredData.map(fruit => (
 *           <li key={fruit}>{fruit}</li>
 *         ))}
 *         </ul>
 *       </div>
 *     </div>
 *   )
 * }
 * <Fruits data={data} />
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-controls
 * @see https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-live
 * @see http://web-accessibility.carnegiemuseums.org/code/search/
 */

export const SearchInput = forwardRef<
  TextInputImperativeHandle,
  SearchInputProps
>(
  (
    {
      collapsible = false,
      context,
      controlsHtmlId,
      id,
      initialValue,
      labelText = 'Search',
      onFocus,
      onKeyDown,
      onSearch,
      placeholder,
      shape = 'pill',
      styleOverrides,
      theme: propTheme,
      value,
    },
    externalRef
  ) => {
    const [isHovered, setIsHovered] = useState(false)
    const [inFocus, setInFocus] = useState(false)
    const [hasValue, setHasValue] = useState(!!initialValue || !!value)
    const inputRef = useRef<TextInputImperativeHandle>(null)

    const providedTheme = useTheme()
    const theme = propTheme ?? providedTheme

    const handleChange = (value: string): void => {
      setHasValue(Boolean(value))
      onSearch?.(value)
    }

    // When using an externally controlled value,
    // ensure that `hasValue` is updated accordingly
    useEffect(() => {
      if (value !== undefined) {
        setHasValue(!!value)
      }
    }, [value])

    const canBeCollapsed = collapsible
      ? !(inFocus || isHovered) && !hasValue
      : false

    return (
      <StrictMode>
        <form
          aria-label={context}
          className="lapidary-searchInput"
          onSubmit={blockSubmit}
          role="search"
        >
          <TextInputWithLabel
            clearable={true}
            controlsHtmlId={controlsHtmlId}
            enterKeyHint="search"
            id={id}
            initialValue={initialValue}
            labelText={labelText}
            leadingIconGlyph="gem-search"
            onInputBlur={() => setInFocus(false)}
            onChange={handleChange}
            onInputFocus={(e) => {
              setInFocus(true)
              onFocus?.(e)
            }}
            onContainerMouseOut={() => setIsHovered(false)}
            onContainerMouseOver={() => setIsHovered(true)}
            onKeyDown={onKeyDown}
            placeholder={placeholder || labelText}
            ref={externalRef ?? inputRef}
            styleOverrides={{
              input: styleOverrides?.input,
              inputContainer: [
                styles.container,
                shape === 'square' ? styles.square(theme) : undefined,
                canBeCollapsed ? styles.collapsed : undefined,
                styleOverrides?.inputContainer,
              ].flat(),
              label: screenreaderOnly,
            }}
            theme={theme}
            type={TextInputType.Search}
            value={value}
          />
        </form>
      </StrictMode>
    )
  }
)

SearchInput.displayName = 'Lapidary: SearchInput'
